import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import {
  deleteDraftJournalEntry,
  fetchJournalEntryById,
  postJournalEntry,
  reverseJournalEntry,
  saveDraftJournalEntry,
} from '@/renderer/services/journalService';
import {
  deleteJournalEntryLine,
  saveJournalEntryLine,
} from '@/renderer/services/journalLineService';
import { apiError, apiSuccess } from '@/renderer/util/notify';
import type { JournalEntryFormState } from '@/renderer/components/dashboard/accounting/journals/journalForm';
import type {
  JournalEntryRecord,
  SaveJournalEntryRequest,
} from '@/renderer/types/accounting';

type SaveJournalVariables = {
  companyId: number;
  outletId: string | null;
  editingEntry: JournalEntryRecord | null;
  form: JournalEntryFormState;
};

export function useSaveJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, SaveJournalVariables>({
    mutationFn: async ({ companyId, outletId, editingEntry, form }) => {
      const payload: SaveJournalEntryRequest = {
        id: editingEntry?.id,
        companyId,
        outletId:
          form.postingScope === 'outlet' ? form.outletId || outletId : null,
        postingScope: form.postingScope,
        referenceType: form.referenceType || null,
        referenceId: form.referenceId || null,
        sourceModule: form.sourceModule,
        entryDate: form.entryDate,
        memo: form.memo || null,
      };

      const entryResponse = await saveDraftJournalEntry(payload);
      if (!entryResponse.success || !entryResponse.data) {
        throw new Error(entryResponse.error ?? 'Failed to save journal entry');
      }

      const journalEntryId = entryResponse.data.id;
      const retainedLineIds = new Set(
        form.lines.map((line) => line.id).filter(Boolean),
      );

      if (editingEntry) {
        const existingResponse = await fetchJournalEntryById(
          companyId,
          journalEntryId,
        );
        if (!existingResponse.success || !existingResponse.data) {
          throw new Error(
            existingResponse.error ?? 'Failed to reload journal entry lines',
          );
        }

        const linesToDelete = existingResponse.data.lines.filter(
          (line) => !retainedLineIds.has(line.id),
        );
        const deleteResponses = await Promise.all(
          linesToDelete.map((line) =>
            deleteJournalEntryLine(companyId, journalEntryId, line.id),
          ),
        );
        const deleteFailure = deleteResponses.find((r) => !r.success);
        if (deleteFailure) {
          throw new Error(
            deleteFailure.error ?? 'Failed to delete a removed journal line',
          );
        }
      }

      const lineResponses = await Promise.all(
        form.lines.map((line) =>
          saveJournalEntryLine({
            id: line.id,
            companyId,
            journalEntryId,
            accountId: line.accountId,
            outletId:
              form.postingScope === 'outlet' ? form.outletId || outletId : null,
            debit: Number(line.debit || 0),
            credit: Number(line.credit || 0),
            description: line.description || null,
          }),
        ),
      );
      const lineFailure = lineResponses.find((r) => !r.success);
      if (lineFailure) {
        throw new Error(lineFailure.error ?? 'Failed to save journal line');
      }
    },
    onSuccess: (_, { editingEntry }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounting.all });
      apiSuccess(
        editingEntry ? 'Journal draft updated.' : 'Journal draft created.',
      );
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}

export function usePostJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      entryId,
    }: {
      companyId: number;
      entryId: string;
    }) => {
      const result = await postJournalEntry(companyId, entryId);
      if (!result.success)
        throw new Error(result.error ?? 'Failed to post journal entry');
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounting.all });
      apiSuccess('Journal entry posted.');
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}

export function useReverseJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      entryId,
      reversalDate,
      memo,
    }: {
      companyId: number;
      entryId: string;
      reversalDate: string;
      memo: string;
    }) => {
      const result = await reverseJournalEntry(
        companyId,
        entryId,
        reversalDate,
        memo,
      );
      if (!result.success)
        throw new Error(result.error ?? 'Failed to reverse journal entry');
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounting.all });
      apiSuccess('Journal entry reversed.');
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}

export function useDeleteJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      entryId,
    }: {
      companyId: number;
      entryId: string;
    }) => {
      const result = await deleteDraftJournalEntry(companyId, entryId);
      if (!result.success)
        throw new Error(result.error ?? 'Failed to delete journal draft');
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounting.all });
      apiSuccess('Journal draft deleted.');
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}
